import { EmissionEstimationIcons } from '@lune-fe/lune-components-lib'
import { AutocompleteSelect, LuneThemeNew, Text, Tile } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import {
    airTransportModeArray,
    inlandWaterwaysTransportModeArray,
    railTransportModeArray,
    roadTransportModeArray,
    seaTransportModeArray,
    TransportMode,
    TransportModeGroup,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'

type ShippingMethodGroupSelectProps = {
    selectedGroup?: TransportModeGroup
    onUpdate: (group: TransportModeGroup | undefined) => void
    preSelectedShippingMethod?: TransportMode
}

type ListOption = {
    value: TransportModeGroup
    label: string
    icon: string
}

const options: ListOption[] = [
    {
        label: 'Sea',
        value: 'Sea',
        icon: 'ship',
    },
    {
        label: 'Air',
        value: 'Air',
        icon: 'plane',
    },
    {
        label: 'Road',
        value: 'Road',
        icon: 'truck',
    },
    {
        label: 'Rail',
        value: 'Rail',
        icon: 'train',
    },
    {
        label: 'Inland waterways',
        value: 'Inland waterways',
        icon: 'waterway',
    },
]

export const shippingMethodGroupMapping: Record<TransportModeGroup, readonly TransportMode[]> = {
    Sea: seaTransportModeArray,
    Air: airTransportModeArray,
    Road: roadTransportModeArray,
    Rail: railTransportModeArray,
    'Inland waterways': inlandWaterwaysTransportModeArray,
}

const { palette } = LuneThemeNew

const ShippingMethodGroupSelect = ({
    selectedGroup: parentSelectedGroup,
    onUpdate,
    preSelectedShippingMethod,
}: ShippingMethodGroupSelectProps) => {
    const [localSelected, setLocalSelected] = useState<string | null>(null)

    const selectedGroup = useMemo(() => {
        if (parentSelectedGroup) {
            return parentSelectedGroup
        }

        if (!preSelectedShippingMethod) {
            return undefined
        }

        // Find which group the pre-selected shipping method belongs to
        for (const [group, modes] of Object.entries(shippingMethodGroupMapping)) {
            if (modes.includes(preSelectedShippingMethod)) {
                return group
            }
        }

        return undefined
    }, [preSelectedShippingMethod, localSelected, parentSelectedGroup])

    useEffect(() => {
        if (preSelectedShippingMethod) {
            setLocalSelected(null)
        }
    }, [preSelectedShippingMethod])

    const final = useMemo(() => {
        return options.find((option) => option.value === (selectedGroup || localSelected)) || null
    }, [selectedGroup, localSelected])

    const handleGroupChange = (group: TransportModeGroup | undefined) => {
        onUpdate(group)
    }

    return (
        <Box sx={{ mb: 3 }}>
            <AutocompleteSelect
                paperSx={{ minWidth: '220px' }}
                label={'Select transport mode group'}
                onChange={(selected: ListOption | null) => {
                    setLocalSelected(selected?.value || null)
                    handleGroupChange(selected?.value)
                }}
                value={final}
                items={options}
                customOptions={(option) => {
                    return (
                        <Tile
                            icon={
                                EmissionEstimationIcons[
                                    option.icon as keyof typeof EmissionEstimationIcons
                                ]
                            }
                            sx={{ '*': { color: palette.Grey900 } }}
                        >
                            <Text variant={'body3'}>{option.label}</Text>
                        </Tile>
                    )
                }}
            />
        </Box>
    )
}

export default ShippingMethodGroupSelect
